import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { DataWithPagingMetadata } from '~/infrastructure/paging';
import { PageState } from '~/shared/enums/stock-files.enum';
import { fileSources } from '~/shared/enums/fileSources';
import { FilesService } from '~/services/modules/stocks/files';
import { GDataService } from '~/services/GDataService';
import { EnvironmentLoaderService } from '~/services/BackEndURL/environment-loader.service';

/**
 * @component DocumentManagerComponent
 * @description
 * Shared component for managing attached files (Documents, Pictures, Videos)
 * across transactions, orders, assets, and catalog entities.
 * Supports attached files grid, thumbnail preview popup, primary picture selection,
 * direct file downloads, library modal selection, and upload dialog integration.
 */
@Component({
    selector: 'asap-document-manager, asap-file-manager',
    templateUrl: './document-manager.component.html',
    styleUrls: ['./document-manager.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DocumentManagerComponent implements OnInit, OnChanges, OnDestroy {
  /** Type of files being managed ('document' | 'picture' | 'video') */
  @Input() fileType: 'document' | 'picture' | 'video' = 'document';

  /** Array of attached file records */
  @Input() documents: any[] = [];
  @Output() documentsChange = new EventEmitter<any[]>();

  /** Current page state (e.g. PageState.VIEW to disable edits) */
  @Input() pageState: PageState = PageState.ADD;

  /** Explicit disabled flag */
  @Input() disabled: boolean = false;

  /** Whether to show the card/section title */
  @Input() showTitle: boolean = true;

  /** Custom title displayed in the heading (defaults based on fileType) */
  @Input() title?: string;

  /** Custom button text for adding files (defaults based on fileType) */
  @Input() addButtonText?: string;

  /** Custom button text for library selection */
  @Input() libraryButtonText: string = 'Select from Library';

  /** Name of the entity used in dialog titles (e.g. 'Perform Maintenance') */
  @Input() entityTitle?: string;

  /** Whether to show a Primary checkbox column (used for picture galleries) */
  @Input() showPrimary: boolean = false;

  /** Emitted when an item is marked as primary */
  @Output() primaryChange = new EventEmitter<any>();

  /** Storage path for file downloads & thumbnail previews */
  _storagePath: string = '';

  /** File library selection window state */
  _showDocumentsGrid: boolean = false;
  _selectedDocuments: any[] = [];
  _selectedObjectDocuments: any[] = [];
  _selectedDocumentsKeys: any[] = [];

  /** Add file dialog state */
  _isDialog: boolean = false;
  _dialogComponentName: string = 'documents';
  _dialogModel: any = null;

  private _dataTransferSub?: Subscription;

  get isReadOnly(): boolean {
    return this.disabled || this.pageState === PageState.VIEW;
  }

  get displayTitle(): string {
    if (this.title) return this.title;
    switch (this.fileType) {
      case 'picture': return 'Pictures';
      case 'video': return 'Videos';
      default: return 'Documents';
    }
  }

  get displayAddButtonText(): string {
    if (this.addButtonText) return this.addButtonText;
    switch (this.fileType) {
      case 'picture': return 'Add Picture';
      case 'video': return 'Add Video';
      default: return 'Add Documents';
    }
  }

  get libraryDialogTitle(): string {
    const typeLabel = this.fileType === 'picture' ? 'Picture(s)' : (this.fileType === 'video' ? 'Video(s)' : 'Document(s)');
    const entity = this.entityTitle || 'Transaction';
    return `Add ${typeLabel} to ${entity}`;
  }

  get selectByFieldTitle(): string {
    return this.fileType === 'picture' ? 'Select Picture' : (this.fileType === 'video' ? 'Select Video' : 'Select Document');
  }

  constructor(
    private _filesService: FilesService,
    private _dataService: GDataService
  ) {}

  ngOnInit(): void {
    this._storagePath = (EnvironmentLoaderService.envConfig?.enterpriseApiLocal || '') + '/';
    this.syncSelectedKeys();

    this._dataTransferSub = this._dataService.dataTransferObservable.subscribe((str: any) => {
      const matchesComponent = str?.component === this._dialogComponentName ||
                               (this.fileType === 'picture' && str?.component === 'files') ||
                               (this.fileType === 'document' && str?.component === 'documents');

      if (str?.isAttachment && str?.response && matchesComponent) {
        this._addDocument(str);
        this._isDialog = false;
      }
    });
  }

  ngOnDestroy(): void {
    this._dataTransferSub?.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['documents']) {
      this.syncSelectedKeys();
    }
  }

  private syncSelectedKeys(): void {
    if (this.documents && this.documents.length > 0) {
      this._selectedDocumentsKeys = this.documents
        .map((d: any) => d.fileId || d.fileid || d.id || d.file?.id)
        .filter((id: any) => id != null);
    } else {
      this._selectedDocumentsKeys = [];
    }
  }

  // #region File Library Selection
  _selectDocumentsHandler(e?: any): void {
    this._showDocumentsGrid = true;
  }

  _closeDocumentsWindow(): void {
    this._showDocumentsGrid = false;
  }

  _returnSelectedDocumentsHandler(e?: any): void {
    this._showDocumentsGrid = false;
    this._selectedDocumentsKeys = [...this._selectedDocuments];
    this._addDocument();
  }

  _addDocument(fileData: any = null): void {
    const currentDocs = [...(this.documents || [])];

    if (fileData == null) {
      if (this._selectedObjectDocuments.length > 0) {
        this._selectedObjectDocuments.forEach((element: any) => {
          const targetId = Number(element.id) || undefined;
          const targetName = element.name;
          const exists = targetId
            ? currentDocs.some((f: any) => {
                const fId = Number(f.fileId || f.fileid || f.id || f.file?.id);
                return fId === targetId;
              })
            : (targetName ? currentDocs.some((f: any) => f.name === targetName || f.file?.name === targetName) : false);

          if (!exists) {
            currentDocs.push({
              fileId: element.id,
              fileid: element.id,
              id: element.id,
              file: element,
              name: element.name,
              description: element.description,
              type: element.type,
              stream: element.stream,
              thumbnail: element.thumbnail,
              isPrimary: false,
              assetFileisPrimary: false
            });
          }
        });
      }
    } else {
      const resp = fileData.response || fileData;
      const fileObj = fileData.picObject?.file || fileData.file || resp;
      const targetId = Number(resp?.id || fileData?.id || fileData?.picObject?.id || fileObj?.id) || undefined;
      const targetName = fileData?.name || resp?.name || fileObj?.name;
      const exists = targetId
        ? currentDocs.some((f: any) => {
            const fId = Number(f.fileId || f.fileid || f.id || f.file?.id);
            return fId === targetId;
          })
        : (targetName ? currentDocs.some((f: any) => f.name === targetName || f.file?.name === targetName) : false);

      if (!exists) {
        currentDocs.push({
          fileId: targetId,
          fileid: targetId,
          id: targetId,
          file: fileObj,
          name: targetName,
          description: fileData?.description || resp?.description || fileObj?.description,
          type: fileData?.type || resp?.type || fileObj?.type,
          stream: fileData?.stream || resp?.stream || fileObj?.stream,
          thumbnail: fileData?.thumbnail || resp?.thumbnail || fileObj?.thumbnail,
          isPrimary: false,
          assetFileisPrimary: false
        });
      }
    }

    this.documents = currentDocs;
    this.syncSelectedKeys();
    this.documentsChange.emit(this.documents);
  }

  _removeDocHandler(event: any): void {
    const currentDocs = [...(this.documents || [])];
    const index = currentDocs.indexOf(event);
    if (index > -1) {
      currentDocs.splice(index, 1);
    }
    const targetId = event.fileId || event.fileid || event.id || event.file?.id;
    const keyIdx = this._selectedDocumentsKeys.indexOf(targetId);
    if (keyIdx > -1) {
      this._selectedDocumentsKeys.splice(keyIdx, 1);
    }

    this.documents = currentDocs;
    this.syncSelectedKeys();
    this.documentsChange.emit(this.documents);
  }

  _setPrimary(item: any): void {
    if (this.isReadOnly) return;
    this.documents.forEach((doc: any) => {
      const isMatch = doc === item;
      doc.isPrimary = isMatch;
      doc.assetFileisPrimary = isMatch;
    });
    this.documentsChange.emit(this.documents);
    this.primaryChange.emit(item);
  }

  _onDocumentsRowSelectionChanges(e: any[]): void {
    this._selectedDocuments = [];
    if (e) {
      e.forEach((item) => this._selectedDocuments.push(+item));
    }
  }

  _onDocumentObjectRowSelectionChange(e: any[]): void {
    this._selectedObjectDocuments = e || [];
  }

  _getAccountFiles = (params?: any, model?: any): Observable<DataWithPagingMetadata<any>> => {
    if (this.fileType === 'picture') {
      return this._filesService.getPictures(params, model);
    } else if (this.fileType === 'video') {
      return this._filesService.getVideos(params, model);
    }
    return this._filesService.getDocuments(params, model);
  };

  /** Alias for backward compatibility */
  _getAccountDocuments = this._getAccountFiles;

  _doDownloadFile(event: any): void {
    const stream = event?.file?.stream || event?.stream;
    if (!stream) return;
    const filePath = this._storagePath + stream;
    const a = document.createElement('a');
    a.href = filePath;
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
  // #endregion

  // #region Add File Dialog
  _openAddDocumentDialog(): void {
    this._isDialog = true;

    if (this.fileType === 'picture') {
      this._dialogComponentName = 'files';
      this._dialogModel = {
        model: {},
        pageState: PageState.ADD,
        reload: 'model',
        fileSource: fileSources.pictures
      };
    } else {
      this._dialogComponentName = 'documents';
      this._dialogModel = {
        model: {},
        pageState: PageState.ADD,
        isDocument: true,
        reload: 'model',
        fileSource: fileSources.documents
      };
    }
  }

  _closeDialog(event: any): void {
    if (typeof event === 'boolean') {
      this._isDialog = false;
    } else {
      this._isDialog = event?.open ?? false;
    }

    const matches = event?.component === this._dialogComponentName ||
                    (this.fileType === 'picture' && event?.component === 'files') ||
                    (this.fileType === 'document' && event?.component === 'documents');

    if (matches && event?.response) {
      this._addDocument(event);
    }
  }
  // #endregion
}
